import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";

const FxContext = createContext(null);

const FALLBACK_RATES = { TRY: 1, USD: 34, EUR: 37 };
const FALLBACK_CHANGES = { USD: null, EUR: null };
const FALLBACK_GOLD = { gram_try: null, change_pct: null, updated_at: null, source: "n/a" };
// Mirror existing core/security.py path rules; the server remains authoritative.
const FX_READ_ROLES = new Set(['admin', 'muhasebe', 'viewer', 'kademe', 'santiye', 'sirket_muduru', 'bayi', 'sevkiyat', 'operator']);
const FX_REFRESH_ROLES = new Set(['admin', 'muhasebe', 'viewer']);

export function FxProvider({ children }) {
  const { user } = useAuth();
  const [rates, setRates] = useState(FALLBACK_RATES);
  const [changes, setChanges] = useState(FALLBACK_CHANGES);
  const [gold, setGold] = useState(FALLBACK_GOLD);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [source, setSource] = useState("fallback");
  const [refreshing, setRefreshing] = useState(false);
  const pending = useRef(null);
  const userId = user?.id;
  const role = user?.role;
  const canReadFx = !!userId && FX_READ_ROLES.has(role);

  const refresh = useCallback(async (force = false) => {
    if (!canReadFx) return;
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setRefreshing(true);
    try {
      const forceAllowed = force && FX_REFRESH_ROLES.has(role);
      const { data } = await api.request({ url: forceAllowed ? '/fx/refresh' : '/fx', method: forceAllowed ? 'post' : 'get', signal: controller.signal });
      if (controller.signal.aborted || pending.current !== controller) return;
      if (data?.rates_to_try) setRates({ TRY: 1, ...data.rates_to_try });
      if (data?.changes_pct) setChanges(data.changes_pct);
      if (data?.gold) setGold(data.gold);
      setUpdatedAt(data?.updated_at || null);
      setSource(data?.source || "fallback");
    } catch {
      /* keep fallback */
    } finally {
      if (!controller.signal.aborted && pending.current === controller) setRefreshing(false);
    }
  }, [canReadFx, role]);

  useEffect(() => {
    setRates(FALLBACK_RATES); setChanges(FALLBACK_CHANGES); setGold(FALLBACK_GOLD);
    setUpdatedAt(null); setSource('fallback'); setRefreshing(false);
    refresh();
    return () => { pending.current?.abort(); };
  }, [refresh, userId]);

  // Convert amount+currency → TRY using current live rates
  const toTry = (amount, currency) => {
    const a = Number(amount);
    if (!isFinite(a)) return 0;
    return a * (rates[currency || "TRY"] || 1);
  };

  return (
    <FxContext.Provider value={{ rates, changes, gold, updatedAt, source, refresh, refreshing, toTry, canReadFx }}>
      {children}
    </FxContext.Provider>
  );
}

export const useFx = () => useContext(FxContext);
